// Audio Source section. Used to be a standalone top-right card; now exposes
// buildSourceSection() that returns a DOM node ready to be wrapped inside a
// MenuSheet collapsibleSection.
//
// SECURITY: never use innerHTML — DOM is built via createElement +
// textContent only. The hookify rule blocks innerHTML at edit time.

const STYLE_ID = 'dp-source-style';

const STYLE = `
.dp-source-buttons { display: flex; gap: 6px; }
.dp-source-buttons button {
  flex: 1; padding: 10px 8px;
  background: #1d2128; color: #e8ecf3;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 5px; cursor: pointer;
  font: 12px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  min-height: 38px;
}
.dp-source-buttons button:hover:not(:disabled) { background: #262b34; border-color: rgba(255,255,255,0.25); }
.dp-source-buttons button.active { background: #1f3a55; border-color: #6af; color: #fff; }
.dp-source-buttons button:disabled { opacity: 0.5; cursor: wait; }

.dp-source-drop {
  margin-top: 10px; padding: 12px; border: 1px dashed rgba(255,255,255,0.2);
  text-align: center; color: rgba(255,255,255,0.55); font-size: 11px; border-radius: 5px;
}
.dp-source-drop.drag-over { border-color: #6af; color: #e8ecf3; }

.dp-source-status {
  margin-top: 10px; color: rgba(255,255,255,0.55); font-size: 11px;
  font-family: ui-monospace, monospace;
}
.dp-source-error {
  margin-top: 6px; color: #f66; font-size: 11px;
  font-family: ui-monospace, monospace;
}

.dp-source-hotkeys {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,0.08);
  display: flex; flex-wrap: wrap; gap: 4px 6px;
  font: 10px/1.4 ui-monospace, SFMono-Regular, monospace;
  letter-spacing: 0.02em;
}
.dp-source-hotkey {
  flex: 0 0 auto;
  padding: 5px 8px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  font: inherit;
  min-height: 32px;
}
.dp-source-hotkey:hover:not(.disabled) { background: rgba(255,255,255,0.12); color: #fff; border-color: rgba(255,255,255,0.3); }
.dp-source-hotkey.disabled { opacity: 0.3; cursor: not-allowed; }
.dp-source-hotkey .dp-source-hotkey-key { color: #6af; font-weight: 600; margin-right: 4px; }
`;

// Each chip dispatches a synthetic keydown for its `key`. `fractalOnly: true`
// chips dim out when the cathedral mode is active. The C chip's label flips
// to show what pressing it WILL do next.
const HOTKEYS = [
  { key: 'F', label: 'Next fractal', fractalOnly: true },
  { key: 'P', label: 'Palette', fractalOnly: true },
  { key: 'V', label: 'View', fractalOnly: true },
  { key: 'C', label: 'Mode', dynamicLabel: (mode) => mode === 'fractal' ? '→ Cathedral' : '→ Fractal' },
  { key: 'D', label: 'Debug' },
  { key: 'Z', label: 'Fullscreen' },
];

export class SourcePicker {
  constructor({ engine, sourceManager, modeSwitcher }) {
    this.engine = engine;
    this.sm = sourceManager;
    this.modeSwitcher = modeSwitcher || null;
    this.refs = {};
  }

  // Returns a DOM node containing all source-picker UI, ready to be embedded
  // inside the MenuSheet's Source section.
  buildSourceSection() {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = STYLE;
      document.head.appendChild(style);
    }

    const root = document.createElement('div');

    const buttons = document.createElement('div');
    buttons.className = 'dp-source-buttons';
    for (const src of ['demo', 'file', 'mic', 'tab']) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.src = src;
      btn.textContent = src.charAt(0).toUpperCase() + src.slice(1);
      btn.addEventListener('click', () => this._onButton(src));
      buttons.appendChild(btn);
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'audio/*';
    fileInput.hidden = true;
    fileInput.addEventListener('change', (e) => {
      const f = e.target.files && e.target.files[0];
      if (f) this._run('file', () => this.sm.selectFile(f), f.name);
    });

    const drop = document.createElement('div');
    drop.className = 'dp-source-drop';
    drop.textContent = 'drop .mp3/.wav here';
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('drag-over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('drag-over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('drag-over');
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (!f) return;
      const isAudio = (f.type && f.type.startsWith('audio/')) || /\.(mp3|wav|ogg|m4a|flac)$/i.test(f.name);
      if (!isAudio) { this._showError('Not an audio file'); return; }
      this._run('file', () => this.sm.selectFile(f), f.name);
    });

    const status = document.createElement('div');
    status.className = 'dp-source-status';
    status.textContent = '— no source —';

    const error = document.createElement('div');
    error.className = 'dp-source-error';
    error.hidden = true;

    const hotkeys = document.createElement('div');
    hotkeys.className = 'dp-source-hotkeys';
    for (const entry of HOTKEYS) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'dp-source-hotkey';
      chip.dataset.key = entry.key;
      if (entry.fractalOnly) chip.dataset.fractalOnly = '1';
      chip.title = `Press ${entry.key}`;
      const keySpan = document.createElement('span');
      keySpan.className = 'dp-source-hotkey-key';
      keySpan.textContent = `(${entry.key})`;
      const labelSpan = document.createElement('span');
      labelSpan.className = 'dp-source-hotkey-label';
      labelSpan.textContent = entry.label;
      chip.appendChild(keySpan);
      chip.appendChild(labelSpan);
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        if (chip.classList.contains('disabled')) return;
        window.dispatchEvent(new KeyboardEvent('keydown', { key: entry.key, bubbles: true }));
      });
      hotkeys.appendChild(chip);
    }

    root.append(buttons, fileInput, drop, status, error, hotkeys);
    this.refs = { root, buttons, fileInput, drop, status, error, hotkeys };

    const initialMode = this.modeSwitcher?.getCurrent?.() || 'fractal';
    this._renderChipState(initialMode);
    if (this.modeSwitcher) {
      const prevOnModeChange = this.modeSwitcher.onModeChange;
      this.modeSwitcher.onModeChange = (mode) => {
        this._renderChipState(mode);
        if (typeof prevOnModeChange === 'function') prevOnModeChange(mode);
      };
    }

    return root;
  }

  destroy() {
    // No global listeners owned by this class anymore — the MenuSheet
    // controls visibility. Nothing to clean up here.
  }

  _onButton(src) {
    this._clearError();
    if (src === 'file') { this.refs.fileInput.click(); return; }
    if (src === 'demo') this._run('demo', () => this.sm.selectDemo(), 'demo track');
    else if (src === 'mic') this._run('mic', () => this.sm.selectMic(), 'mic');
    else if (src === 'tab') this._run('tab', () => this.sm.selectTab(), 'tab');
  }

  async _run(src, fn, displayName) {
    if (this.sm.isLoading) return;
    this._setButtonsDisabled(true);
    try {
      await fn();
      const sourceNow = this.engine.status && this.engine.status.source;
      if (sourceNow === 'none') {
        this.refs.status.textContent = '— no source —';
        this._setActiveButton(null);
      } else {
        this.refs.status.textContent = `now playing: ${displayName}`;
        this._setActiveButton(src);
      }
      this._clearError();
    } catch (err) {
      if (err && err.name !== 'AlreadyLoading') {
        this._showError((err && err.message) || String(err));
      }
    } finally {
      this._setButtonsDisabled(false);
    }
  }

  _setButtonsDisabled(disabled) {
    for (const btn of this.refs.buttons.querySelectorAll('button[data-src]')) {
      btn.disabled = disabled;
    }
  }

  _setActiveButton(src) {
    for (const btn of this.refs.buttons.querySelectorAll('button[data-src]')) {
      btn.classList.toggle('active', btn.dataset.src === src);
    }
  }

  _renderChipState(mode) {
    for (const chip of this.refs.hotkeys.querySelectorAll('button[data-key]')) {
      const key = chip.dataset.key;
      const entry = HOTKEYS.find((h) => h.key === key);
      if (!entry) continue;
      const labelSpan = chip.querySelector('.dp-source-hotkey-label');
      if (entry.dynamicLabel && labelSpan) {
        labelSpan.textContent = entry.dynamicLabel(mode);
      }
      if (entry.fractalOnly) {
        chip.classList.toggle('disabled', mode !== 'fractal');
      }
    }
  }

  _showError(msg) {
    this.refs.error.textContent = msg;
    this.refs.error.hidden = false;
  }

  _clearError() {
    this.refs.error.textContent = '';
    this.refs.error.hidden = true;
  }
}
